import type { FeedbackPriority } from "@keydials/api-client";

/**
 * The half-written feedback report that hasn't been sent yet.
 *
 * There is no server-side draft: `submitBugReport` / `submitFeatureRequest` take the whole report in
 * one go. So localStorage is the ONLY thing between a half-typed report and an accidental reload —
 * which is exactly what used to happen, because the modal reset every field whenever it opened.
 *
 * Ported from web-admin's `feedbackDraft.ts` (KEY-327), trimmed to the draft half: dock-position
 * storage already exists in web-user as `help/dockPosition.ts` (built for the Help videos panel,
 * KEY-393), so this file reuses that rather than duplicating the clamp/position logic.
 *
 * Kept as a pure module, apart from the components, for two reasons: web-user has no component-test
 * setup (no jsdom, no testing-library — every test here is a node-env test of a plain `.ts`), and the
 * fiddly parts are all in here anyway. Every read and write is wrapped: storage can be full, blocked
 * or disabled, and losing a draft must never take the app down with it.
 */

const DRAFT_KEY = "kde.feedback-draft.v1";
/** Each floating panel remembers its own place. Sharing one key would move both at once, and they
 *  can be on screen together — the feedback dock over the Help videos panel, say. */
export const FEEDBACK_DOCK_POSITION_KEY = "kde.feedback-dock-position.v1";

export type FeedbackTab = "Bug" | "Feature";

export interface FeedbackDraft {
  tab: FeedbackTab;
  priority: FeedbackPriority;
  whatWereYouDoing: string;
  whatDidYouExpect: string;
  whatActuallyHappened: string;
  description: string;
}

export const emptyFeedbackDraft: FeedbackDraft = {
  tab: "Bug",
  priority: "Normal",
  whatWereYouDoing: "",
  whatDidYouExpect: "",
  whatActuallyHappened: "",
  description: "",
};

/**
 * Whether there is anything worth keeping.
 *
 * Priority and tab are deliberately NOT counted: they always have a value, so counting them would
 * make every untouched form look like a draft in progress — and the collapsed pill would wear a
 * "Draft" badge from the moment the dock first rendered.
 */
export function draftHasContent(draft: FeedbackDraft): boolean {
  return (
    draft.whatWereYouDoing.trim().length > 0 ||
    draft.whatDidYouExpect.trim().length > 0 ||
    draft.whatActuallyHappened.trim().length > 0 ||
    draft.description.trim().length > 0
  );
}

/** A stored value that should be a string. Anything else becomes "" — see loadFeedbackDraft. */
function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function priorityOf(value: unknown): FeedbackPriority {
  return value === "Urgent" || value === "Low" || value === "Normal" ? value : "Normal";
}

/**
 * Reads back a stored draft, validating FIELD BY FIELD rather than trusting the parse.
 *
 * This is not defensiveness for its own sake: every field feeds a controlled `<textarea>`, and a
 * stored `undefined` reaching one flips it to uncontrolled — React then warns and the field stops
 * tracking state. A partial or hand-edited payload has to come back as a complete draft or not at all.
 */
export function loadFeedbackDraft(): FeedbackDraft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const d = parsed as Partial<Record<keyof FeedbackDraft, unknown>>;
    return {
      tab: d.tab === "Feature" ? "Feature" : "Bug",
      priority: priorityOf(d.priority),
      whatWereYouDoing: text(d.whatWereYouDoing),
      whatDidYouExpect: text(d.whatDidYouExpect),
      whatActuallyHappened: text(d.whatActuallyHappened),
      description: text(d.description),
    };
  } catch {
    return null;
  }
}

/**
 * Persists a draft — or REMOVES the key when there is nothing in it. Leaving an empty husk behind
 * would mean the dock reopens carrying a "draft" that is four empty strings.
 */
export function saveFeedbackDraft(draft: FeedbackDraft): void {
  try {
    if (!draftHasContent(draft)) {
      localStorage.removeItem(DRAFT_KEY);
      return;
    }
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // Storage full, blocked, or disabled. A lost draft is bad; a crash is worse.
  }
}

export function clearFeedbackDraft(): void {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {
    /* see saveFeedbackDraft */
  }
}
